"use client";

import { useState } from "react";
import { Checkbox, TextAreaField } from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import { StyledResult } from "@/components/tools/text/styled-result";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  applyStyle,
  NEGATIVE_SQUARED_MAP,
  requestTextPresentation,
  SQUARED_MAP,
  unstyledCharacters,
} from "@/lib/tool-kit/text/unicode-styles";

/** English strings. A translated content file overrides these through `ui`. */
export const SQUARED_TEXT_GENERATOR_STRINGS = {
  inputLabel: "Your text",
  inputPlaceholder: "Type or paste the text you want in squares",
  trySample: "Try a sample",
  sample: "Game over",
  clear: "Clear",
  results: "Results",
  squaredLabel: "Squared (🄰🄱🄲)",
  negativeLabel: "Negative squared (🅰🅱🅲)",
  capitalsHint:
    "Unicode has squared capitals only, so lowercase letters are drawn as capitals.",
  emojiHint:
    "🅰 🅱 🅾 🅿 are also emoji, so some apps draw them as colored buttons that stand out from the other letters.",
  textStyle:
    "Ask apps to draw 🅰 🅱 🅾 🅿 as plain letters (adds the invisible text-style selector U+FE0E after each)",
  emptyResult: "Type above to see the result.",
  copy: "Copy",
  copied: "Copied!",
  unchanged:
    "Left as they were — Unicode has no squared version of these: {list}",
  accessibility:
    'Screen readers may announce each square by its symbol name ("squared Latin capital letter G") or skip it, and search treats these characters as different from ordinary letters.',
} as const;

export default function SquaredTextGeneratorWidget() {
  const strings = useToolUi(SQUARED_TEXT_GENERATOR_STRINGS);
  const [input, setInput] = useState("");
  const [textStyle, setTextStyle] = useState(false);
  const squared = applyStyle(input, SQUARED_MAP);
  const negativeStyled = applyStyle(input, NEGATIVE_SQUARED_MAP);
  const negative = textStyle
    ? requestTextPresentation(negativeStyled)
    : negativeStyled;
  const unchanged = unstyledCharacters(input, SQUARED_MAP);

  return (
    <>
      <TextAreaField
        id="squared-input"
        label={strings.inputLabel}
        onChange={setInput}
        placeholder={strings.inputPlaceholder}
        prose
        rows={4}
        value={input}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={input.length === 0}
          onClick={() => setInput("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <section aria-live="polite" className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <StyledResult
          copiedLabel={strings.copied}
          copyLabel={strings.copy}
          emptyText={strings.emptyResult}
          hint={strings.capitalsHint}
          label={strings.squaredLabel}
          value={squared}
        />
        <StyledResult
          copiedLabel={strings.copied}
          copyLabel={strings.copy}
          emptyText={strings.emptyResult}
          hint={strings.emojiHint}
          label={strings.negativeLabel}
          value={negative}
        />
        <Checkbox
          checked={textStyle}
          label={strings.textStyle}
          onChange={setTextStyle}
        />
        {unchanged.length > 0 ? (
          <p className={HINT}>
            {t(strings.unchanged, { list: unchanged.join(" ") })}
          </p>
        ) : null}
        <p className={HINT}>{strings.accessibility}</p>
      </section>
    </>
  );
}
